'use client';

import React from 'react';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiButton from '@new-components/atoms/MuiButton';
import MuiDialog, {
  MuiDialogContent,
  MuiDialogTitle,
} from '@new-components/atoms/MuiDialog';
import MuiSvgIcon from '@new-components/atoms/MuiSvgIcon';
import MuiTypography from '@new-components/atoms/MuiTypography';
import RegisterInterestInfo, {
  BreadcrumbItem,
} from '@new-components/molecules/RegisterInterestInfo';
import RegisterInterestSuccess from '@new-components/molecules/RegisterInterestSuccess';
import {LeadForm, LeadFormProps} from '@new-components/organisms/LeadForm';
import {InputNames} from '@new-components/organisms/LeadForm/FormConfig';
import {InputNames as BrochureInputNames} from '@new-components/organisms/BrochureLeadForm/FormConfig';
import {requestLead} from '@services/new/common/requestLead';
import {getAppRoute} from '@utils/getAppRoute';
import {useIsDesktop} from '@utils/useisdesktop';
import {navigate} from '@utils/navigate';
import {useRouter} from 'next/navigation';
import {Zoom} from '@mui/material';
import {ImageObj} from '@custom-types/ImageObj.types';
import {BrochureLeadForm} from '@new-components/organisms/BrochureLeadForm';
import {createDownloadLink} from '@utils/createDownloadLink';
import {
  trackFileDownload,
  trackFormEvents,
} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {datadogLogs} from '@datadog/browser-logs';
import {getFileData} from '@services/new/common/getFileData';
import {isValidUrl} from '@damascus-components/CommunityDetailSection/ViewBrochureButton/ViewBrochureButton';
import {ImgProps} from '@custom-types/ImgProps';
import Image from 'next/image';
import {useLeadSourceParams} from '@hooks/useLeadSourceParams';

export type AdditionalLeadProps = {
  id: string | null;
  reserveNow?: boolean;
  brochureLink?: string;
  communityName?: string;
  propertyInfo?: string;
  reservationImg?: ImgProps;
};
type Props = Omit<LeadFormProps, 'onSubmit'> &
  AdditionalLeadProps & {
    breadcrumbs: BreadcrumbItem[];
    open: boolean;
    onClose: () => void;
  };
export type FormLeadPayload = Record<InputNames, string | boolean>;
export type BrochureFormLeadPayload = Record<
  BrochureInputNames,
  string | boolean
>;
type FormStatus = 'idle' | 'loading' | 'success' | 'error';
export function LeadGeneration({breadcrumbs, open, onClose, ...props}: Props) {
  const isDesktop = useIsDesktop();
  const [formStatus, setStatus] = React.useState<FormStatus>('idle');
  const [leadResponse, setLeadResponse] = React.useState<{
    primaryMessage?: string;
    secondaryMessage?: string;
    image?: ImageObj;
  }>({});
  const errorCount = React.useRef(0);
  const {getLeadSourceParams} = useLeadSourceParams();

  React.useEffect(() => {
    if (open) return;
    setStatus('idle');
    setLeadResponse({});
    errorCount.current = 0;
  }, [open]);

  const getFormBasedPayload = (
    payload: FormLeadPayload | BrochureFormLeadPayload
  ) => {
    const leadType = {
      unit: 1,
      community: 2,
    };
    const leadSourceParams = getLeadSourceParams();
    switch (props.formType) {
      // case 'CONTACT_FORM': {
      //   return {...payload, id: null};
      // }
      case 'BROCHURE_FORM':
      case 'LSQ_BROCHURE_FORM': {
        return {
          ...payload,
          ...leadSourceParams,
          id: props.id,
          reserveNow: props.reserveNow,
          type: leadType['community'],
        };
      }
      case 'PROPERTY_FORM':
      case 'LSQ_PROPERTY_FORM': {
        return {
          ...payload,
          ...leadSourceParams,
          id: props.id,
          unitID: props.id,
          reserveNow: props.reserveNow,
          type: leadType['unit'],
        };
      }
      case 'COMMUNITY_FORM':
      case 'LSQ_COMMUNITY_FORM':
        return {
          ...payload,
          ...leadSourceParams,
          id: props.id,
          reserveNow: props.reserveNow,
          type: leadType['community'],
        };
      case 'RESALE_FORM': {
        return {
          ...payload,
          ...leadSourceParams,
          id: props.id,
          type: leadType['community'],
        };
      }
      default:
        return {...payload, ...leadSourceParams};
    }
  };
  const onSubmit = async (data: FormLeadPayload) => {
    const payload = getFormBasedPayload(data);
    try {
      setStatus('loading');
      const {message} = await requestLead(payload).send();
      setLeadResponse({
        primaryMessage: message.title,
        secondaryMessage: message.description,
        image: message.image,
      });
      setStatus('success');
      trackFormEvents('success');
    } catch (error) {
      setStatus('error');
      trackFormEvents('fail');
    }
  };
  const handleBrouchureAction = async (type: 'view' | 'download') => {
    const brochureLink = props.brochureLink;
    if (!brochureLink || !isValidUrl(brochureLink)) {
      throw new Error('Brochure link is required');
    }
    let link: HTMLAnchorElement | null = null;
    if (type === 'view') {
      link = document.createElement('a');
      link.href = brochureLink;
      link.setAttribute('target', '_blank');
    } else {
      try {
        const fileData = await getFileData(brochureLink).send();
        link = createDownloadLink(
          fileData as Blob,
          'Brochure'
        ) as HTMLAnchorElement;
        trackFileDownload('success', {fileUrl: brochureLink});
      } catch (error) {
        datadogLogs.logger.error('Error fetching file data:', {error});
        trackFileDownload('fail', {fileUrl: brochureLink});
        return;
      }
    }
    if (link) {
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const onBrochureSubmit = async (
    data: BrochureFormLeadPayload,
    type: 'view' | 'download'
  ) => {
    const payload = getFormBasedPayload(data);
    try {
      setStatus('loading');
      await requestLead(payload).send();
      const message = {
        title: '<h4 style="text-align:center;">Thank you!</h4>',
        description: `<p style=\"text-align:center;\">We're thrilled that you've chosen to download/view our ${props.communityName} brochure and express your interest in Aldar Properties.The brochure will be downloaded/open in a few seconds.</p>`,
      };
      setLeadResponse({
        primaryMessage: message.title,
        secondaryMessage: message.description,
      });
      setStatus('success');
      trackFormEvents('success');
    } catch (error) {
      setStatus('error');
      trackFormEvents('fail');
    } finally {
      try {
        await handleBrouchureAction(type);
      } catch (error) {
        setStatus('error');
      }
    }
  };
  const onRetry = () => {
    setStatus('idle');
    errorCount.current = errorCount.current + 1;
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      fullScreen={!isDesktop}
      scroll='paper'
      PaperProps={{
        sx: props.reservationImg && {
          maxWidth: {xs: '100%', md: '920px', lg: '1100px'},
        },
      }}>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', md: 'row'},
          overflow: {
            xs: ['success', 'error'].includes(formStatus) ? 'hidden' : 'unset',
            md: 'hidden',
          },
        }}>
        {props.reservationImg && (
          <MuiBox
            sx={{
              flex: 1,
              minHeight: {xs: 'auto', md: '100%'},
              overflow: 'hidden',
              display: {xs: 'none', md: 'block'},
            }}>
            <Image
              src={props.reservationImg.src}
              alt={props.reservationImg.alt}
              width={572}
              height={664}
              style={{height: '100%', objectFit: 'cover'}}
            />
          </MuiBox>
        )}
        <MuiBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: props.reservationImg && {md: '598px'},
            width: props.reservationImg && {md: '50%'},
            overflow: 'auto',
          }}>
          <MuiButton
            sx={{
              backgroundColor: 'rgba(95, 89, 89, 0.8)',
              backgroundBlendMode: 'overlay',
              backdropFilter: 'blur(80px)',
              width: '32px',
              height: '32px',
              borderRadius: '100px',
              minWidth: '32px',
              margin: '12px 24px -32px auto',
            }}
            onClick={onClose}>
            <MuiSvgIcon>
              <svg
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M14.2504 3.75L3.75037 14.25M3.75037 3.75L14.2504 14.25'
                  stroke='white'
                  strokeWidth='1.25'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </MuiSvgIcon>
          </MuiButton>
          <MuiDialogTitle
            sx={{
              fontSize: '24px',
              fontWeight: 500,
              lineHeight: '30px',
              letterSpacing: '-0.4px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: '#000000',
            }}>
            {['BROCHURE_FORM', 'LSQ_BROCHURE_FORM'].includes(props.formType)
              ? 'View Brochure'
              : props.reserveNow
              ? 'Reserve Your Project'
              : 'Register Interest'}
          </MuiDialogTitle>
          {props.reservationImg && (
            <MuiBox sx={{display: {xs: 'block', md: 'none'}, width: '100%'}}>
              <Image
                src={props.reservationImg.src}
                alt={props.reservationImg.alt}
                width={572}
                height={664}
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
              />
            </MuiBox>
          )}
          <MuiDialogContent
            sx={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
            <RegisterInterestInfo
              breadcrumbs={breadcrumbs}
              label={props.propertyInfo}
            />
            {['BROCHURE_FORM', 'LSQ_BROCHURE_FORM'].includes(props.formType) ? (
              <BrochureLeadForm
                {...props}
                onSubmit={onBrochureSubmit}
                loading={formStatus === 'loading'}
              />
            ) : (
              <LeadForm
                {...props}
                onSubmit={onSubmit}
                loading={formStatus === 'loading'}
              />
            )}
          </MuiDialogContent>

          <ModalOverlayController
            open={formStatus === 'success'}
            sideways={props.reserveNow && !!props.reservationImg?.src}>
            <RegisterInterestSuccess
              primaryMessage={leadResponse.primaryMessage}
              secondaryMessage={leadResponse.secondaryMessage}
              image={leadResponse.image}
              handleOnClick={() => {
                setStatus('idle');
                onClose();
              }}
            />
          </ModalOverlayController>
          <ModalOverlayController
            open={formStatus === 'error'}
            sideways={props.reserveNow && !!props.reservationImg?.src}>
            <RegisterInterestError
              onClick={onRetry}
              errorCount={errorCount.current}
            />
          </ModalOverlayController>
        </MuiBox>
      </MuiBox>
    </MuiDialog>
  );
}

const DEFAULT_ERROR_MSG = "Something went sideways. Let's try that again.";
function RegisterInterestError(props: {
  onClick: () => void;
  errorMessage?: string;
  errorCount: number;
}) {
  const router = useRouter();
  const retryLimitExceded = props.errorCount >= 2;
  const errorMessage = !retryLimitExceded
    ? props.errorMessage ?? DEFAULT_ERROR_MSG
    : `We're having trouble completing your request. Please go to the homepage and try again later.`;
  const buttonLabel = !retryLimitExceded ? 'Retry' : 'Back to Homepage';
  const onClickButton = !retryLimitExceded
    ? props.onClick
    : () => {
        navigate(router, getAppRoute('HOME', 'new-home'));
      };
  return (
    <MuiBox
      sx={{
        backgroundColor: '#FFFFFF',
        width: {
          sm: '548px',
          xs: '90vw',
        },
        borderRadius: '16px',
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        height: '100%',
        overflow:"auto"
      }}>
      <MuiBox
        sx={{
          display: 'grid',
          gridTemplateRows: '1fr 1fr',
          flex: 1,
        }}>
        <MuiBox
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
          <MuiTypography
            sx={{
              fontSize: {
                xs: '24px',
                sm: '32px',
              },
              fontWeight: 500,
              color: '#000000',
              letterSpacing: '-0.8px',
              marginBottom: '8px',
            }}>
            {'Oops!'}
          </MuiTypography>
          <MuiTypography
            sx={{
              fontSize: {
                xs: '14px',
                sm: '17px',
              },
              fontWeight: 400,
              color: '#000000',
              letterSpacing: '-0.4px',
              textAlign: 'center',
            }}>
            {errorMessage}
          </MuiTypography>
        </MuiBox>
        <MuiBox
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}>
          {!retryLimitExceded && (
            <MuiTypography
              variant='subtitle2'
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '14px',
                },
                fontWeight: 400,
                color: '#000000',
                letterSpacing: '-0.4px',
                textAlign: 'center',
              }}>
              {`If this persists please consider refreshing the whole page and try again`}
            </MuiTypography>
          )}
        </MuiBox>
      </MuiBox>
      <MuiButton
        fullWidth
        sx={{
          'backgroundColor': '#2D2D2D',
          'color': '#FFFFFF',
          'fontSize': {
            xs: '15px',
            sm: '17px',
          },
          'fontWeight': 400,
          'letterSpacing': '-0.4px',
          'boxShadow': '0px 8px 32px 0px #00000033',
          'transition':
            'box-shadow 0.4s ease-in-out, color 0.4s ease-in-out, transform 0.1s',
          '&:hover': {
            backgroundColor: '#2D2D2D',
            boxShadow: '0px 8px 32px 0px #00000033, rgba(255, 255, 255, 1)',
          },
          '&:active': {
            transform: 'scale(1.1, 1.1)',
          },
        }}
        onClick={onClickButton}>
        {buttonLabel}
      </MuiButton>
    </MuiBox>
  );
}

function ModalOverlayController(props: {
  open: boolean;
  children: React.ReactNode;
  sideways?: boolean;
}) {
  return (
    <Zoom in={props.open}>
      <MuiBox
        sx={{
          position: 'absolute',
          height: '100%',
          maxWidth: props.sideways ? {xs: 'auto', md: '598px'} : 'unset',
          width: props.sideways ? {width: '100%', md: '50%'} : '100%',
          backgroundColor: '#FFFFFF',
          zIndex: 1,
          ['& > div']: {width: '100%'},
        }}>
        {props.children}
      </MuiBox>
    </Zoom>
  );
}
